import ClassNames from "classnames";
import { useEffect, useMemo } from "react";
import { shallowEqual } from "react-redux";
import {
  selectPartnerLogoCategories,
  selectTranslatedPartner,
} from "../../selectors/partnerLogos.js";
import { selectPictureById } from "../../selectors/pictures.js";
import { getActiveSite, getPrimaryDomain } from "../../selectors/sites.js";
import { getAccommodation } from "../../slices/accommodation.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/footer-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  FooterLayoutVariant,
  FooterModuleSettings,
  HeaderModuleSettings,
  Language,
  Module,
  PageState,
} from "../../types/index.js";
import { schemaOrgLodgingTypes } from "../../utils/data.js";
import { css } from "../../utils/emotion.js";
import {
  getActiveColorScheme,
  getActiveLogoId,
  getTranslatedPage,
  getTranslations,
  isLegalNavId,
} from "../../utils/utils.js";
import InlineTerms from "../InlineTerms.js";
import JsonLd from "../JsonLd.js";
import LegalNavLink from "../LegalNavLink.js";
import PartnerLogos from "../PartnerLogos.js";
import RichEditorWrapper from "../RichEditorWrapper.js";
import TrustYou from "../TrustYou.js";

type Props = BaseModuleProps<FooterModuleSettings>;

const translations = {
  de: { vat: "MwSt.-Nr.", region: "Südtirol" },
  it: { vat: "Part. IVA", region: "Alto Adige" },
  en: { vat: "VAT-No.", region: "South Tyrol" },
  fr: { vat: "numéro de TVA", region: "Sud-Tyrol" },
};

const _isBig = (layoutVariant: FooterLayoutVariant) => layoutVariant === "big";

const logoSizeParams = { height: 120 };

const FooterModule: React.FunctionComponent<Props> = ({
  translatedModule,
  translatedModule: {
    siteId,
    translation: {
      languageId,
      settings: { title, description },
    },
    settings,
    settings: { layoutVariant, logoCategories },
  },
  isPreview,
  queries,
  pageId,
}) => {
  const dispatch = useAppDispatch();

  const i18n = getTranslations(languageId, translations);
  const legalNav = useAppSelector(
    (state) =>
      state.pages.systemPages
        .map((pageId) => state.pages.byId[pageId]?.alias ?? null)
        .filter(isLegalNavId),
    shallowEqual,
  );
  const mainDescription = useAppSelector((state) =>
    getMainPageDescription(state.pages, languageId),
  );
  const accommodation = useAppSelector(
    (state) => state.accommodation[languageId],
  );
  const activeLogoId = useAppSelector((state) => {
    const headerModuleId = state.modules.bySiteModuleType.HeaderModule?.[0];
    const headerModule = headerModuleId
      ? (state.modules.byId[headerModuleId] as Module<HeaderModuleSettings>)
      : undefined;
    return getActiveLogoId(
      state.mediaLibrary.logoIds,
      headerModule?.settings.logoId,
    );
  });
  const logo = useAppSelector((state) =>
    selectPictureById(state, activeLogoId, logoSizeParams),
  );
  const partnerLogoCategories = useAppSelector((state) =>
    selectPartnerLogoCategories(state, logoCategories, languageId),
  );
  const trustYouParams = useMemo(
    () => ({ partnerId: "trust-you", includeLogos: true, languageId }),
    [languageId],
  );
  const trustYou = useAppSelector((state) =>
    selectTranslatedPartner(state, trustYouParams),
  );
  const trustYouLogo = trustYou?.logos[0];

  const site = useAppSelector((state) => getActiveSite(state.sites));
  const { vatId } = site;
  const domain = getPrimaryDomain(site);

  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );

  useEffect(() => {
    dispatch(getAccommodation({ siteId, languageId }));
  }, [siteId, languageId]);

  const hotelPhoto =
    accommodation && accommodation.pictures[0]
      ? accommodation.pictures[0].url
      : null;

  const isBig = _isBig(layoutVariant);

  return (
    <footer
      className={ClassNames(
        styles.FooterModule,
        { [styles["FooterModule--big"]]: layoutVariant === "big" },
        moduleBaseStyles.Module,
        css({ background: scheme.main.background, color: scheme.main.text }),
      )}
      id={translatedModule.id}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        {isBig && (
          <div
            className={ClassNames(
              styles.FooterModule__Top,
              css({ borderColor: scheme.primary.background }),
            )}
          >
            <PartnerLogos
              partnerLogos={partnerLogoCategories[0] ?? []}
              logoSize={settings.logoSize}
            />
          </div>
        )}
        <div
          className={ClassNames(
            styles.FooterModule__Main,
            css({ borderColor: scheme.primary.background }),
          )}
        >
          <section className={styles.FooterModule__Section}>
            {title && (
              <h2
                className={ClassNames(
                  styles.FooterModule__Title,
                  `TextAlign--${settings.textAlign.title}`,
                )}
              >
                {title}
              </h2>
            )}
            {accommodation && (
              <div className={`TextAlign--${settings.textAlign.description}`}>
                {!description && (
                  <div className="RichTextCopySource">
                    <div>{accommodation.name}</div>
                    <div>{accommodation.street}</div>
                    <div>
                      {accommodation.zip} {accommodation.city}
                    </div>
                    <div>Tel. {accommodation.phone}</div>
                    <div>
                      <a href={"mailto:" + accommodation.email}>
                        {accommodation.email}
                      </a>
                    </div>
                  </div>
                )}

                <RichEditorWrapper
                  pageId={pageId}
                  languageId={languageId}
                  module={translatedModule}
                  isPreview={isPreview}
                  scheme={undefined}
                  maxCharacters={250}
                />

                <JsonLd
                  data={{
                    "@type": schemaOrgLodgingTypes[accommodation.hotelType],
                    description: mainDescription,
                    name: accommodation.name,
                    address: {
                      "@type": "PostalAddress",
                      addressCountry: "IT",
                      addressLocality: accommodation.city,
                      addressRegion: i18n.region,
                      postalCode: accommodation.zip,
                      streetAddress: accommodation.street,
                    },
                    logo: logo.url,
                    url: `https://${domain}/`,
                    photo: hotelPhoto,
                    image: hotelPhoto,
                    aggregateRating: accommodation.trustYou
                      ? {
                          "@type": "AggregateRating",
                          ratingCount: accommodation.trustYou.count,
                          ratingValue: accommodation.trustYou.value,
                          bestRating: 5,
                        }
                      : null,
                    telephone: accommodation.phone,
                    email: accommodation.email,
                    faxNumber: accommodation.fax,
                    vatID: vatId,
                    starRating: {
                      "@type": "Rating",
                      ratingValue: accommodation.stars,
                    },
                    checkInTime: accommodation.checkIn.from,
                    checkOutTime: accommodation.checkOut.to,
                    petsAllowed:
                      (accommodation.featureMap & 32768) +
                        (accommodation.featureMap & 33554432) >
                      0,
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: accommodation.latitude,
                      longitude: accommodation.longitude,
                    },
                    priceRange: accommodation.priceRange,
                    currenciesAccepted: "EUR",
                    amenityFeature: accommodation.features.map((feature) => ({
                      "@type": "LocationFeatureSpecification",
                      name: feature.title,
                      value: true,
                    })),
                  }}
                />
              </div>
            )}
          </section>
          {!isBig && (
            <PartnerLogos
              partnerLogos={partnerLogoCategories[0] ?? []}
              logoSize={settings.logoSize}
              compactOrder="last"
            />
          )}
          {isBig && (
            <PartnerLogos
              partnerLogos={partnerLogoCategories[1] ?? []}
              logoSize={settings.logoSize}
              compactOrder="last"
            />
          )}
          {settings.showTrustYou && accommodation && accommodation.trustYou && (
            <div className={styles.FooterModule__Widgets}>
              <TrustYou
                {...accommodation.trustYou}
                logo={trustYouLogo}
                languageId={languageId}
              />
            </div>
          )}
        </div>
        <div className={styles.FooterModule__Sub}>
          {!isBig && (
            <PartnerLogos
              partnerLogos={partnerLogoCategories[1] ?? []}
              logoSize={settings.logoSize}
              compactOrder="first"
            />
          )}
          <ul className="Nav">
            {!!accommodation?.cin.length && (
              <li>CIN: {accommodation.cin.join(", ")}</li>
            )}
            {vatId && (
              <li>
                {i18n.vat} {vatId}
              </li>
            )}
            {legalNav.map((id) => (
              <li key={id}>
                <LegalNavLink
                  legalNavId={id}
                  languageId={languageId}
                  isPreview={isPreview}
                />
              </li>
            ))}

            <li className={styles.FooterModule__Hgv}>
              Website by{" "}
              <a href="https://www.hgv.it/" target="_blank" rel="noopener">
                HGV
              </a>
            </li>
          </ul>
        </div>
        <InlineTerms
          siteId={siteId}
          languageId={languageId}
          colorScheme={scheme}
          isPreview={isPreview}
          queries={queries}
          isActive={false}
          isFirstOnPage={false}
        />
      </div>
    </footer>
  );
};

const getMainPageDescription = (
  pages: PageState,
  languageId: Language,
): string | null => {
  try {
    const { description } = getTranslatedPage(
      pages,
      pages.byParentId["null"][0] ?? "",
      languageId,
    ).translation;
    return description;
  } catch (error) {
    // This means the main page is not translated in
    // this language
    return null;
  }
};

export default FooterModule;
